import React from 'react';
import hs from './header-status.module.scss';

const CARD_GAP = 8;
const VIEWPORT_INSET = 8;
/* The card is two short lines; measuring it would cost a layout pass and a
   second render, and being ~8px out on the flip decision is invisible. */
const CARD_HEIGHT_ESTIMATE = 72;
const CARD_WIDTH_ESTIMATE = 260;

export type PlanTier = 'free' | 'basic' | 'standard' | 'pro';

const RAIL_CLASS: Record<PlanTier, string> = {
    free: hs.hoverRailFree,
    basic: hs.hoverRailBasic,
    standard: hs.hoverRailStandard,
    pro: hs.hoverRailPro,
};

export interface PlanHoverTarget {
    top: number;
    bottom: number;
    left: number;
    right: number;
}

interface Props {
    target: PlanHoverTarget;
    /** The entitlement line the badge stopped printing (existing i18n strings). */
    feature: string;
    /** Top tier only — there is nothing left to sell, so it gets the reassurance. */
    status?: string;
    /** Which tier's badge this card belongs to; decides the rail colour. */
    variant: PlanTier;
    isRtl: boolean;
}

/**
 * What the plan badge cannot show: what the plan actually gives you.
 *
 * The badge is one line, because a two-line badge was a card compressed into a
 * chip. The detail lives here instead — in OUR surface. Deliberately not a
 * native `title`: every `title` was stripped out of the calendar because the
 * browser's black tooltip double-painted over our own hover card, and this chip
 * is the most prominent one on the page. The chip's `aria-label` carries the
 * same strings for assistive tech, so this card is purely the sighted path.
 */
export const PlanHoverCard: React.FC<Props> = ({ target, feature, status, variant, isRtl }) => {
    // Viewport coordinates, so the card lands identically in the light DOM and
    // inside the extension's shadow root.
    const viewportWidth = typeof window === 'undefined' ? 1280 : window.innerWidth;
    const viewportHeight = typeof window === 'undefined' ? 800 : window.innerHeight;

    // The badge lives in a header with room beneath it, so this flips
    // vertically where the calendar's card flips horizontally.
    const below = target.bottom + CARD_GAP;
    const top = below + CARD_HEIGHT_ESTIMATE > viewportHeight - VIEWPORT_INSET
        ? Math.max(VIEWPORT_INSET, target.top - CARD_GAP - CARD_HEIGHT_ESTIMATE)
        : below;

    // Aligned to the badge's inline-start edge — which is its right edge in RTL.
    const rawLeft = isRtl ? target.right - CARD_WIDTH_ESTIMATE : target.left;
    const left = Math.min(
        Math.max(VIEWPORT_INSET, rawLeft),
        Math.max(VIEWPORT_INSET, viewportWidth - CARD_WIDTH_ESTIMATE - VIEWPORT_INSET),
    );

    return (
        <div className={hs.hoverCard} style={{ top, left }} role="tooltip">
            <span className={`${hs.hoverRail} ${RAIL_CLASS[variant]}`} />
            <div className={hs.hoverText}>
                <div className={hs.hoverFeature}>{feature}</div>
                {status && <div className={hs.hoverStatus}>{status}</div>}
            </div>
        </div>
    );
};

export default PlanHoverCard;
